import { Link } from 'react-router-dom';
import type { PodcastEpisodeView } from '@almanac/contracts';
import { cn } from '@/lib/utils';
import { episodeDay, latestByFeed } from '../../lib/podcast';
import { EpisodeStrip } from './EpisodeCard';
import { usePodcastLibrary } from './hooks';

/**
 * The episodes published on a digest's day, above the digest. Renders nothing
 * while loading, on error, or when no episode came out that day: the digest is
 * the page, the episode an extra.
 */
export function DayEpisodes({ date, className }: { date: string; className?: string }) {
  const { state } = usePodcastLibrary();
  if (state.status !== 'ready') return null;
  const episodes = state.library.episodes.filter((episode) => episodeDay(episode) === date);
  if (episodes.length === 0) return null;
  return (
    <div className={cn('grid gap-3', episodes.length > 1 && 'md:grid-cols-2', className)}>
      {episodes.map((episode) => (
        <EpisodeStrip
          key={`${episode.feed}:${episode.guid}`}
          episode={episode}
          heading={episode.feed === 'personal' ? 'Your brief came out' : 'New episode'}
        />
      ))}
    </div>
  );
}

/** The newest episode of each feed, for the home page. */
export function LatestEpisodes({
  episodes,
  className,
}: {
  episodes: PodcastEpisodeView[];
  className?: string;
}) {
  const latest = latestByFeed(episodes);
  if (latest.length === 0) return null;
  return (
    <section aria-labelledby="home-latest-episodes" className={className}>
      <div className="flex items-baseline justify-between gap-3">
        <h2
          id="home-latest-episodes"
          className="font-display text-lg font-semibold text-foreground"
        >
          Latest episodes
        </h2>
        <Link to="/podcast" className="text-sm font-medium text-primary hover:underline">
          All episodes →
        </Link>
      </div>
      <div className={cn('mt-3 grid gap-3', latest.length > 1 && 'md:grid-cols-2')}>
        {latest.map((episode) => (
          <EpisodeStrip
            key={`${episode.feed}:${episode.guid}`}
            episode={episode}
            heading={episode.feed === 'personal' ? 'Your weekly brief' : 'Company podcast'}
          />
        ))}
      </div>
    </section>
  );
}
